/**
 * t667u: autosave for settings cards (no Save button). Text waits ~600 ms after the last keystroke, or saves on blur;
 * selects, switches and colours save straight away. Saves run one after another (the latest value wins), and the
 * field shows a quiet "Saving…" / "Saved", or the error with Retry. Modal dialogs keep Save / Cancel.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { errorMessage } from '@/lib/api';

export type AutosaveState = 'idle' | 'saving' | 'saved' | 'error';
export const AUTOSAVE_DELAY = 600;

export interface Autosave<T> {
  state: AutosaveState;
  error: string | null;
  /** Text: save after the pause. */
  schedule: (v: T) => void;
  /** Blur: save a pending value now. */
  flush: () => void;
  /** Selects, switches: save now. */
  now: (v: T) => void;
  retry: () => void;
}

export function useAutosave<T>(save: (v: T) => Promise<unknown>, delay = AUTOSAVE_DELAY): Autosave<T> {
  const [state, setState] = useState<AutosaveState>('idle');
  const [error, setError] = useState<string | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<{ v: T } | null>(null);
  const last = useRef<{ v: T } | null>(null);
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const live = useRef(true);
  const inflight = useRef(0);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);

  const run = useCallback((v: T) => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    pending.current = null;
    last.current = { v };
    setState('saving'); setError(null);
    const start = () => { try { return Promise.resolve(saveRef.current(v)); } catch (e) { return Promise.reject(e); } };
    // Straight away when nothing is saving; otherwise after the save in flight.
    const job = inflight.current ? chain.current.catch(() => undefined).then(start) : start();
    inflight.current += 1;
    chain.current = job;
    void job.finally(() => { inflight.current -= 1; }).catch(() => undefined);
    job.then(
      () => { if (live.current && chain.current === job) setState('saved'); },
      e => { if (live.current && chain.current === job) { setError(errorMessage(e)); setState('error'); } },
    );
  }, []);
  const schedule = useCallback((v: T) => {
    pending.current = { v };
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; if (pending.current) run(pending.current.v); }, delay);
  }, [delay, run]);
  const flush = useCallback(() => { if (pending.current) run(pending.current.v); }, [run]);
  const retry = useCallback(() => { if (last.current) run(last.current.v); }, [run]);
  // Leaving the page (or the tab) with a pending edit saves it.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); if (pending.current) void saveRef.current(pending.current.v).catch(() => undefined); }, []);
  return { state, error, schedule, flush, now: run, retry };
}

/** The quiet status beside a field or in a card header. */
/** No Retry for a value that fails a check: fixing it saves. */
export interface SaveStatusValue { state: AutosaveState; error: string | null; retry?: () => void }
export function SaveStatus({ s, testId }: { s: SaveStatusValue; testId?: string }) {
  return (
    <span aria-live="polite" data-testid={testId} data-state={s.state} className="inline-flex min-h-5 items-center gap-1.5 text-xs">
      {s.state === 'saving' && <span className="text-slate-500">Saving…</span>}
      {s.state === 'saved' && <span className="inline-flex items-center gap-1 text-slate-500"><Check className="h-3.5 w-3.5" />Saved</span>}
      {s.state === 'error' && <>
        <span className="text-rose-600 dark:text-rose-400" role="alert">Not saved: {s.error}</span>
        {s.retry && <button type="button" onClick={s.retry} className="font-medium text-accent-fg underline-offset-2 hover:underline">Retry</button>}
      </>}
    </span>
  );
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export interface AutosaveForm<T> {
  /** The values the card shows (the server's until you edit). */
  draft: T;
  /** Change fields. Text waits for the pause (`now` false); selects, switches and radios pass `now` true. */
  set: (patch: Partial<T> | ((d: T) => T), now?: boolean) => void;
  /** On blur: save a pending text edit now. */
  flush: () => void;
  /** For SaveStatus: saving / saved / the error (a check that failed, or the server's reason, after which the fields go back). */
  status: SaveStatusValue;
}

/**
 * t647u: a settings card that saves itself. `source` is the saved value (undefined while loading); every change saves
 * after the pause (text) or straight away (choices). A value `validate` refuses isn't sent: the reason shows inline
 * until it's fixed. When the server refuses a save, the fields go back to the last saved values and the reason shows
 * with Retry (which puts your change back and tries again). New server values are picked up unless you're mid-edit.
 */
export function useAutosaveForm<T extends object>(source: T | undefined, save: (v: T, prev: T) => Promise<unknown>,
  opts: { validate?: (v: T) => string | null; delay?: number; fallback: T }): AutosaveForm<T> {
  const [draft, setDraft] = useState<T>(source ?? opts.fallback);
  const saved = useRef<T>(source ?? opts.fallback);
  const failed = useRef<T | null>(null);
  const [invalid, setInvalid] = useState<string | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  useEffect(() => {
    // Mid-edit (a change not saved yet, or saving): keep what you typed.
    if (source === undefined || same(source, saved.current) || !same(draftRef.current, saved.current)) return;
    saved.current = source; setDraft(source);
  }, [source]);
  const a = useAutosave<T>(async v => {
    try {
      await save(v, saved.current);
      saved.current = v; failed.current = null;
    } catch (e) {
      failed.current = v;
      // The change didn't stick: show the saved values again.
      if (same(draftRef.current, v)) setDraft(saved.current);
      throw e;
    }
  }, opts.delay);
  const validate = opts.validate;
  const set = useCallback((patch: Partial<T> | ((d: T) => T), now = false) => {
    const next = typeof patch === 'function' ? patch(draftRef.current) : { ...draftRef.current, ...patch };
    draftRef.current = next;
    setDraft(next);
    const bad = validate?.(next) ?? null;
    setInvalid(bad);
    if (bad || same(next, saved.current)) return;
    if (now) a.now(next); else a.schedule(next);
  }, [a, validate]);
  const retry = useCallback(() => {
    const v = failed.current;
    if (!v) return;
    draftRef.current = v; setDraft(v); a.now(v);
  }, [a]);
  const status: SaveStatusValue = invalid ? { state: 'error', error: invalid } : { state: a.state, error: a.error, retry };
  return { draft, set, flush: a.flush, status };
}
